import React, { useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { Col, Row } from "react-bootstrap";
import { FaUserEdit } from "react-icons/fa";
import { FaCircleXmark } from "react-icons/fa6";
import { MdDeleteForever } from "react-icons/md";
import {
  AddButton,
  Button,
  Input,
  SubTitle,
} from "../../Components/CustomComponents";
import DeleteModal from "../../Components/Modals/DeleteModal";
import EditUserModal from "../../Components/Modals/EditUserModal";
import ModalContainer from "../../Components/ModalContainer";
import Table from "../../Components/Table/Table";
import Toast from "../../Hooks/useToast";
import useAgentController from "../../controllers/AgentControllers/useAgentController";
import modalStyles from "../../Components/Modals/styles/editModal.module.scss";
import styles from "./styles/agent_management.module.scss";

const AgentManagement = () => {
  const {
    values: { list: rowData, loading, creating },
    functions: { handleCreateAgent, handleDeleteAgent },
  } = useAgentController();

  const [rowDataOptimistic, setRowDataOptimistic] = useState([]);
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [editedData, setEditedData] = useState("");
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [agentPassword, setAgentPassword] = useState("");

  useEffect(() => {
    if (loading) {
      setIndicationMessage("loading...");
      return;
    }
    setIndicationMessage(rowData.length ? "" : "No agents found");
  }, [loading, rowData.length]);

  const handleDeleteModalOpen = () => setDeleteModal(true);

  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setEditedData("");
  };

  const handleEditModalOpen = () => setEditModal(true);

  const handleEditModalClose = () => {
    setEditModal(false);
    setEditedData("");
  };

  const handleAddModalOpen = () => setAddModal(true);

  const handleAddModalClose = () => {
    setAddModal(false);
    setAgentName("");
    setAgentEmail("");
    setAgentPassword("");
  };

  const getAgentName = (row) => {
    return (
      row.username ||
      row.name ||
      `${row.firstName || ""} ${row.lastName || ""}`.trim()
    );
  };

  const getEmail = (row) => row.email || "-";

  const onConfirmDelete = async (id) => {
    try {
      await handleDeleteAgent(id);
      handleDeleteModalClose();
    } catch {
    }
  };

  const handleAgentUpdate = (updatedAgent) => {
    setRowDataOptimistic((prevRows) =>
      prevRows.map((item) =>
        item.id === updatedAgent.id ? { ...item, ...updatedAgent } : item
      )
    );
    Toast("Agent updated successfully", "success");
  };

  useEffect(() => {
    setRowDataOptimistic(rowData);
  }, [rowData]);

  const handleAddAgent = async () => {
    if (!agentName.trim() || !agentEmail.trim() || !agentPassword.trim()) {
      Toast("Please fill name, email and password", "error");
      return;
    }

    try {
      await handleCreateAgent({
        name: agentName,
        email: agentEmail,
        password: agentPassword,
      });
      handleAddModalClose();
    } catch {
      /* Toast in controller */
    }
  };

  function indication() {
    return indicationMessage;
  }

  const displayRows = rowDataOptimistic;

  const columns = [
    {
      dataField: "username",
      text: "Name",
      sort: true,
      headerStyle: {
        width: "280px",
      },
      formatter: (cellContent, row) => {
        const imageUrl = row.image_url || row.image;
        const name = getAgentName(row) || "Agent";

        return (
          <div className={styles.agentCell}>
            <img
              src={imageUrl}
              alt={name}
              className={styles.agentAvatar}
              loading="lazy"
            />
            <span>{name}</span>
          </div>
        );
      },
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      headerStyle: {
        width: "320px",
      },
      formatter: (cellContent, row) => getEmail(row),
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "220px",
      },
      formatter: (cellContent, row) => {
        if (!row.id) return null;

        return (
          <div className="d-flex justify-content-center align-items-center">
            <div
              className={`${styles.btnBg} d-flex justify-content-center align-items-center rounded-3 mx-2 p-2`}
              role="button"
              onClick={() => {
                setEditedData(row);
                handleEditModalOpen();
              }}
            >
              <span className="align-items-center d-flex justify-content-center pe-1">
                <FaUserEdit />
              </span>
              <span>Edit</span>
            </div>
            <div
              className="d-flex justify-content-center align-items-center bg-danger rounded-3 p-2 mx-2"
              role="button"
              onClick={() => {
                setEditedData({ ...row, name: getAgentName(row) });
                handleDeleteModalOpen();
              }}
            >
              <span className="d-flex align-items-center pe-1">
                <MdDeleteForever />
              </span>
              <span>Delete</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {deleteModal ? (
        <DeleteModal
          handleDeleteModalClose={handleDeleteModalClose}
          data={editedData}
          onDelete={onConfirmDelete}
        />
      ) : (
        ""
      )}

      {editModal ? (
        <EditUserModal
          handleEditModalClose={handleEditModalClose}
          data={editedData}
          onUpdate={handleAgentUpdate}
        />
      ) : (
        ""
      )}

      {addModal ? (
        <ModalContainer handleClose={handleAddModalClose}>
          <div className={modalStyles.modalContainer}>
            <div className="d-flex align-items-center w-100 justify-content-between mb-4">
              <p className="fs-4 fw-medium m-0">Add Agent</p>
              <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
                <FaCircleXmark
                  role="button"
                  onClick={handleAddModalClose}
                  style={{ cursor: "pointer" }}
                />
              </span>
            </div>

            <div className="w-100">
              <Row>
                <Col md={12}>
                  <p className="m-0">Agent Name</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="text"
                    placeholder="Enter agent name"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Email</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="email"
                    placeholder="Enter agent email"
                    value={agentEmail}
                    onChange={(e) => setAgentEmail(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <p className="m-0">Password</p>
                  <Input
                    classes="mt-2 mb-3"
                    type="password"
                    placeholder="Enter password"
                    value={agentPassword}
                    onChange={(e) => setAgentPassword(e.target.value)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Button
                    classes="mt-4"
                    text={creating ? "Saving..." : "Save Agent"}
                    onClick={handleAddAgent}
                    disabled={creating}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </ModalContainer>
      ) : (
        ""
      )}

      <div className={styles.headerContainer}>
        <div className="w-100 flex-between">
          <SubTitle text={"Agent Management"} />
          <AddButton text="Add Agent" onClick={handleAddModalOpen} />
        </div>
      </div>

      <div className={styles.mainTable}>
        <Table data={displayRows} columns={columns} indication={indication} />
      </div>
    </div>
  );
};

export default AgentManagement;
